import React, {useRef} from 'react';
import styled, {css} from 'styled-components';
import firstBy from 'thenby';

import {ReactComponent as Info} from '@bootcamp/shared/src/assets/svg/warning.svg';

import {useUserDataContext} from '@bootcamp/web/src/contexts/UserData';

import {StyledBody, StyledHeader, StyledLabel, StyledTable} from './shared';
import {SubjectCell, CellText, BarCell, BarCellText, MasteryCountCellText, CellSecondary, CellIcon, CellProgressBar, TopicCell, iconMap} from '@bootcamp/web/src/bootcamps/components/Performance/shared';
import {TableOverflowOuterWrapper, TableOverflowInnerWrapper} from '@bootcamp/web/src/bootcamps/components/Performance/shared';
import {AutoHeightParent} from '@bootcamp/web/src/components/AutoHeightParent';

import theme from '@bootcamp/shared/src/styles/theme';

import {averages, averagesWithPlusTests} from './averages';


const Container = styled.div`
  border-radius: 8px;
`;
const SubjectTable = styled(StyledTable)`
  tr:first-child {
    td {
      white-space: nowrap;
    }
  }
  td:last-child {
    text-align: left;
  }
`;
const SubjectHeader = styled(StyledHeader)`
  ${({theme}) => theme.mediaQueries.tablet} {
    padding: ${({parentHasPadding}) => parentHasPadding ? '' : '0 16px'};
  }
`;

const StyledTableOverflowOuterWrapper = styled(TableOverflowOuterWrapper)`

`;
const StyledTableOverflowInnerWrapper = styled(TableOverflowInnerWrapper)`

`;

const sumCounts = obj => Object.values(obj).reduce((acc, val) => acc + val, 0);

const getSubjectTotal = (counts, subject, hasPlusPackAccess) => {
  try {
    const {baseCounts, plusCounts} = counts || {};

    let total = 0;

    total = sumCounts(baseCounts[subject]);

    if (hasPlusPackAccess) {
      total += sumCounts(plusCounts[subject]);
    }

    return total;

  } catch (error) {
    return 0;
  }
}

const getTopicTotal = (counts, subject, topic, hasPlusPackAccess) => {
  try {
    const {baseCounts, plusCounts} = counts || {};

    let total = 0;

    total = baseCounts[subject][topic];

    if (hasPlusPackAccess) {
      total += plusCounts[subject][topic];
    }

    return total || 0;

  } catch (error) {
    return 0;
  }
}

export const Subject = ({dataBySubject=[], expandAll=false, parentHasPadding, subjectTopicCounts, mastery}) => {
  const tableWrapperRef = useRef(null);
  const {isBootcampPlusStudent} = useUserDataContext();
  const averagesObj = isBootcampPlusStudent ? averagesWithPlusTests : averages;

  const rows = dataBySubject
  .map(({testSubject, correct, total, timeSpent, topics}) => {
    const parentPercentage = Math.round((correct / total) * 100)
    const subjectTotal = getSubjectTotal(subjectTopicCounts, testSubject, isBootcampPlusStudent);
    const subjectAvgScore = Math.round(averagesObj?.[testSubject]?.avgScore / averagesObj?.[testSubject]?.totalQuestions * 100);
    const subjectAvgTagged = Math.round(subjectTotal * 0.75);
    const subjectMasteryTotal = mastery?.[testSubject]?.total|| 0;

    const subjectScoreSegments = [
      {count: subjectTotal * (correct / total), color: theme.colors.brandPalette.blue.default},
    ];

    const subjectMasterySegments = [
      {count: mastery?.[testSubject]?.mastered, spacing: 0},
      {count: mastery?.[testSubject]?.reviewing, spacing: 0},
      {count: mastery?.[testSubject]?.learning, spacing: 0},
    ];

    return {
      parent: [
        {content: (
          <SubjectCell>
            <CellIcon src={iconMap?.dat?.[testSubject]}/>
            <CellText>{testSubject}</CellText>
          </SubjectCell>
        )},
        {content: (
          <BarCell>
            <BarCellText>
              {total ? `${parentPercentage}%` : ' '}
            </BarCellText>
            <CellProgressBar segments={subjectScoreSegments} total={subjectTotal} markPosition={subjectAvgScore}/>
          </BarCell>
        )},
        {content: <CellSecondary>{subjectAvgScore}%</CellSecondary>},
        {content: (
          <BarCell>
            <MasteryCountCellText>{subjectMasteryTotal} / {subjectTotal}</MasteryCountCellText>
            <CellProgressBar segments={subjectMasterySegments} total={subjectTotal} markPosition={75}/>
          </BarCell>
        )},
        {content: <CellSecondary  >{subjectAvgTagged} / {subjectTotal}</CellSecondary>},
      ],
      nested: Object
        .entries(topics)
        .sort(firstBy(([topic]) => topic))
        .map(([topic, dataBySubject]) => {
          const topicPercentage = Math.round(dataBySubject?.correct / dataBySubject?.total * 100);
          const topicTotal = getTopicTotal(subjectTopicCounts, testSubject, topic, isBootcampPlusStudent);

          const topicMasteryTotal = mastery?.[testSubject]?.topics?.[topic]?.total || 0;
          const topicAvgs = averagesObj?.[testSubject]?.systems?.[topic];
          const topicAvgScore = Math.round((topicAvgs?.avgScore / topicAvgs?.totalQuestions) * 100);

          const topicAvgTagged = Math.round(topicTotal * 0.75) || 0;

          const isRed = topicPercentage <= 50;

          const topicScoreSegments = [
            {count: topicTotal * (dataBySubject?.correct / dataBySubject?.total), color: theme.colors.brandPalette.blue.default}
          ];

          const topicMasterySegments = [
            {count: mastery?.[testSubject]?.topics?.[topic]?.mastered, spacing: 0},
            {count: mastery?.[testSubject]?.topics?.[topic]?.reviewing, spacing: 0},
            {count: mastery?.[testSubject]?.topics?.[topic]?.learning, spacing: 0},
          ];

          return {
            onClick: null,
            noHover: true,
            background: theme.colors.backgroundPalette.secondary,
            cells: [
              {content: ''},
              {content: <TopicCell style={{display: 'flex', alignItems: 'center', flexDirection: 'row'}}>{topic} {isRed ? <Info style={{marginLeft: '8px', minWidth: 16}} size={16} color="#E05E57" /> : ''}</TopicCell>},
              {content: (
                <BarCell>
                  <BarCellText>{topicPercentage}%</BarCellText>
                  <CellProgressBar segments={topicScoreSegments} total={topicTotal} markPosition={topicAvgScore}/>
                </BarCell>
              )},
              {content: <CellSecondary>{topicAvgScore}%</CellSecondary>},
              {content: (
                <BarCell>
                  <MasteryCountCellText>{topicMasteryTotal} / {topicTotal}</MasteryCountCellText>
                  <CellProgressBar segments={topicMasterySegments} total={topicTotal} markPosition={75}/>
                </BarCell>
              )},
              {content: <CellSecondary>{topicAvgTagged} / {topicTotal}</CellSecondary>},
            ]
          }
        })
      }
  })
  const totalsAcrossSubjects = dataBySubject.reduce((acc, {correct, total, timeSpent}) => ({
    correct: acc.correct + correct,
    total: acc.total + total,
    timeSpent: acc.timeSpent + timeSpent,
  }), {correct: 0, total: 0, timeSpent: 0})

  return (
    <Container>
      <SubjectHeader parentHasPadding={parentHasPadding}>
        <StyledLabel>Subject Breakdown</StyledLabel>
        <StyledBody>Increase your score in each subject by focusing on topics marked with a red <Info style={{verticalAlign: -3}} size={16} color="#E05E57" /> - that's where you can improve the most!</StyledBody>
        {/* <StyledBlurb icon={ari} text={<span >Topics marked with a red <Info style={{verticalAlign: -3}} size={16} color="#E05E57" /> should receive additional attention.</span>} lightMode/> */}
      </SubjectHeader>
      <AutoHeightParent childRef={tableWrapperRef}>
        <StyledTableOverflowOuterWrapper parentHasPadding={parentHasPadding}>
          <StyledTableOverflowInnerWrapper ref={tableWrapperRef} parentHasPadding={parentHasPadding}>
            <SubjectTable
              expandAll={expandAll}
              hideChevrons
              hideLastCell
              fixed={false}
              rows={[
                { cells: [{ content: '' }, { content: 'Subject' }, { content: 'Score' }, {content: 'Avg. Score', disabled: true}, {content: 'Questions Tagged'}, { content: 'Avg. Tagged', disabled: true}] },
                // {cells: [{content: ''}, {content: 'Total'}, {content: ''}, {content: `${Math.round(totalsAcrossSubjects.correct / (totalsAcrossSubjects.total || 1) * 100)}%`}, {content: `${totalsAcrossSubjects.correct} / ${totalsAcrossSubjects.total}` }, { content: '' }] },
                ...rows
              ]}
            />
          </StyledTableOverflowInnerWrapper>
        </StyledTableOverflowOuterWrapper>
      </AutoHeightParent>
    </Container>
  );
};


Subject.defaultProps = {};
Subject.propTypes = {};

export default Subject;